import React, { useState, useEffect } from 'react';
import { Modal, Box, TextField, Button, MenuItem, CircularProgress } from '@mui/material';
import {
  days,
  categories,
  optionalLocationCategories,
  optionalInstructorCategories,
} from '../constants';

const EMPTY_WORKSHOP = {
  title: '',
  day: '',
  startTime: '',
  endTime: '',
  description: '',
  category: '',
  location: '',
  instructor: '',
};

const WorkshopModal = ({
  open,
  handleClose,
  saveWorkshop,
  workshopToEdit,
  isOrganizer,
}) => {
  const [workshop, setWorkshop] = useState(EMPTY_WORKSHOP);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setWorkshop(workshopToEdit || EMPTY_WORKSHOP);
    setSaving(false);
  }, [workshopToEdit, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setWorkshop((prevState) => ({ ...prevState, [name]: value }));
  };

  const isLocationRequired = !optionalLocationCategories.includes(
    workshop.category
  );
  const isInstructorRequired = !optionalInstructorCategories.includes(
    workshop.category
  );
  const optionalFieldHelperText = `Pole niewymagane w przypadku kategorii '${workshop.category}'.`;

  const handleSubmit = async () => {
    if (!isFormValid() || saving) return;
    setSaving(true);
    try {
      await saveWorkshop(workshop);
    } finally {
      setSaving(false);
    }
  };

  const isFormValid = () => {
    const { title, day, startTime, endTime, location, instructor } = workshop;
    if (!title || !day || !startTime) return false;
    if (endTime && startTime >= endTime) return false;
    if (isJp2Time(startTime) || isJp2Time(endTime)) return false;
    if (isLocationRequired && !location) return false;
    if (isInstructorRequired && !instructor) return false;
    return true;
  };

  const isJp2Time = (time) => time === '21:37';

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          p: 2,
          bgcolor: 'background.paper',
          margin: 'auto',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <h2>{workshopToEdit ? 'Edytuj warsztat' : 'Dodaj warsztat'}</h2>
        <TextField
          fullWidth
          margin="normal"
          label="Tytuł"
          name="title"
          value={workshop.title}
          onChange={handleChange}
          required
          inputProps={{ maxLength: 150 }}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Opis"
          name="description"
          value={workshop.description}
          onChange={handleChange}
          inputProps={{ maxLength: 500 }}
        />
        <TextField
          fullWidth
          select
          margin="normal"
          label="Dzień"
          name="day"
          value={workshop.day}
          onChange={handleChange}
          required
        >
          {days.map((day) => (
            <MenuItem key={day.value} value={day.value}>
              {day.text}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          margin="normal"
          label="Początek"
          name="startTime"
          type="time"
          value={workshop.startTime}
          onChange={handleChange}
          required
          inputProps={{
            step: 60,
          }}
          error={isJp2Time(workshop.startTime)}
          helperText={
            isJp2Time(workshop.startTime)
              ? 'Ta godzina nie przejdzie... 🤓'
              : ''
          }
        />
        <TextField
          fullWidth
          margin="normal"
          label="Koniec"
          name="endTime"
          type="time"
          value={workshop.endTime}
          onChange={handleChange}
          inputProps={{
            step: 60,
          }}
          error={
            (workshop.endTime && workshop.startTime >= workshop.endTime) ||
            isJp2Time(workshop.endTime)
          }
          helperText={
            workshop.endTime && workshop.startTime >= workshop.endTime
              ? 'Koniec wcześniej niż początek? 🤔'
              : isJp2Time(workshop.endTime)
              ? 'Ta godzina nie przejdzie... 🤓'
              : ''
          }
        />
        <TextField
          fullWidth
          select
          margin="normal"
          label="Kategoria"
          name="category"
          value={workshop.category}
          onChange={handleChange}
          required
        >
          {categories
            .filter(
              (category) => isOrganizer || category.name !== 'organizatorzy'
            )
            .map((category) => (
              <MenuItem key={category.name} value={category.name}>
                {category.name}
              </MenuItem>
            ))}
        </TextField>
        <TextField
          fullWidth
          margin="normal"
          label="Miejsce"
          name="location"
          value={workshop.location}
          onChange={handleChange}
          required={isLocationRequired}
          inputProps={{ maxLength: 100 }}
          helperText={isLocationRequired ? '' : optionalFieldHelperText}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Prowadzący"
          name="instructor"
          value={workshop.instructor}
          onChange={handleChange}
          required={isInstructorRequired}
          inputProps={{ maxLength: 100 }}
          helperText={isInstructorRequired ? '' : optionalFieldHelperText}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!isFormValid() || saving}
        >
          {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Zapisz'}
        </Button>
      </Box>
    </Modal>
  );
};

export default WorkshopModal;
